import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import Store from "../../../index";
import { rawApi, api } from "../../../../lib";
import { GetStockAdjustmentsRequest, StockAdjustmentResource, StockAdjustmentListItem, StockAdjustmentDetailListItem, StockAdjustmentDetailResource, StockAdjustmentListFilters } from "@/models/api/barcodeSystem/stockAdjustments/get";
import { EditStockAdjustmentRequest } from "@/models/api/barcodeSystem/stockAdjustments/edit";
import { defaultStockAdjustmentFilters } from "./helpers";
import { PageData, TableField } from "../../../types";
import BarcodeSystemModule from "@/store/modules/barcodeSystem";
import { AxiosResponse } from "axios";
import { ApplyStockAdjustmentRequest } from "../../../../models/api/barcodeSystem/stockAdjustments/apply";
import { formatBool, formatDate } from "../../../../utils";
import * as modals from "@/lib/modals";

const name = "stockAdjustment";
const barcodeSystemModuleState = getModule(BarcodeSystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true, stateFactory: true })
export default class StockAdjustmentModule extends VuexModule {
  public loading: boolean = false;
  public error: boolean = false;
  public errorMessage: string = "";
  public data: StockAdjustmentListItem[] | null = null;
  public currentStockAdjustment: StockAdjustmentResource | null = null;
  public currentStockAdjustmentDetails: StockAdjustmentDetailResource[] | null = null;
  public filters : StockAdjustmentListFilters = defaultStockAdjustmentFilters();
  public pageData: PageData = {
    totalPages: null,
    totalItems: null,
    currentPage: 1,
    pageSize: 30
  };
  public fields: Array<TableField> = [
      { key: "selected", label: "", hidden: false },
      { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
      { key: "createdByUsername", sortable: true, label: "Created By", hidden: false },
      { key: "createdDate", sortable: true, label: "Created Date", formatter: formatDate, hidden: false, noWrap: true},
      { key: "wasApplied", sortable: true, label: "Was Applied", formatter: formatBool, hidden: false },
      { key: "appliedByUsername", sortable: true, label: "Applied By", hidden: false },
      { key: "appliedDate", sortable: true, label: "Update Date", formatter: formatDate, hidden: false, noWrap: true}, 
      { key: "comment", sortable: true, label: "Comment", hidden: false },
  ];
  public dataSortKey: string | null = null;
  public dataSortAscending: boolean | null = null;

  @Action
  async createStockAdjustment(request: EditStockAdjustmentRequest) {
    return await api.postAsync("/barcode/stockAdjustments", request);
  }  
  
  @Action
  async updateStockAdjustment(request: EditStockAdjustmentRequest) {
    return await api.putAsync(`/barcode/stockAdjustments/${request.id}`, request);
  }

  @Action
  async deleteStockAdjustment(id) {
    return await api.deleteAsync(`/barcode/stockAdjustments/${id}`);
  }

  @Action
  async applyStockAdjustment(request: ApplyStockAdjustmentRequest) {
    return await api.postAsync(`/barcode/stockAdjustments/${request.id}/apply`, request);
  }  

  @Action
  async getStockAdjustments(params: GetStockAdjustmentsRequest): Promise<void> {
    const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
    const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

    if (pageNumber === 1) {
      this.resetList();
    }

    let target = `/barcode/stockAdjustments/?page=${pageNumber}&PageSize=${pageSize}`;

    if (this.filters) {
      if (this.filters.id) {
          target += `&id=${this.filters.id}`;
      }
      if (this.filters.facilityCodes) {
        this.filters.facilityCodes.forEach(element => {
          target += `&facilityCodes=${element}`;
        });
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }
      if (this.filters.createdDate) {
          const dateData = this.filters.createdDate.split(" ~ ");
          if(!dateData[1] || dateData[1] === "null")
          {
              dateData[1] = dateData[0];
          }
          target += `&createdDateStart=${dateData[0]}&createdDateEnd=${dateData[1]}`;
      }
      if (this.filters.appliedDate) {
          const dateData = this.filters.appliedDate.split(" ~ ");
          if(!dateData[1] || dateData[1] === "null")
          {
              dateData[1] = dateData[0];
          }
          target += `&appliedDateStart=${dateData[0]}&appliedDateEnd=${dateData[1]}`;
      }
      if (this.filters.createdByUsername) {
          target += `&createdByUsername=${this.filters.createdByUsername}`;
      }
    }
    else if(barcodeSystemModuleState.currentFacilityCode)
    {
        target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
    }

    if (this.dataSortKey) {
      target += `&sortField=${this.dataSortKey}&SortAscending=${
        this.dataSortAscending ? "true" : "false"
      }`;
    }

    try {
      var response = await rawApi.get<StockAdjustmentListItem[]>(target);
      this.setList(response);
      this.setCurrentPage(pageNumber);
    } catch (ex) {
      throw ex;
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async getStockAdjustment(id: number) : Promise<StockAdjustmentResource> {
    var response = await api.getAsync(`/barcode/stockAdjustments/${id}`);
    return response.data;
  } 
  
  @Action
  async getStockAdjustmentDetails(id: number) : Promise<StockAdjustmentDetailResource[]> {
    var response = await api.getAsync(`/barcode/stockAdjustments/${id}/details`);
    return response.data;
  }  

  @Action
  async applyListSort(sort: string) {     
    this.setDataSort(sort);
    await this.getStockAdjustments({
        pageNumber: 1,
        pageSize: this.pageData.pageSize
    });
  }
    
  @Action
  async updateStockAdjustmentsAfterAction(items: any) {
    for (let index = 0; index < items.length; index++) {
      const adjustment = await this.getStockAdjustment(items[index].id);
      this.refreshStockAdjustmentData(adjustment)
    }
  }

  @Action
  async loadStockAdjustment(id: number): Promise<void> {
    const data = await this.getStockAdjustment(id);
    this.setCurrentStockAdjustment(data);
    this.refreshStockAdjustmentData(data);
  }   
   
  @Action
  async loadStockAdjustmentDetails(id: number): Promise<void> {
    this.setCurrentStockAdjustmentDetails(await this.getStockAdjustmentDetails(id));
  } 
   
  @Action
  async reloadStockAdjustment(): Promise<void> {
    if (this.currentStockAdjustment)
    {
      const data = await this.getStockAdjustment(this.currentStockAdjustment.id);
      this.setCurrentStockAdjustment(data);
      this.refreshStockAdjustmentData(data);
    } 
  }

  @Action
  async viewStockAdjustmentShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }
    return this.context.dispatch(
      "system/showModal",
      { name: modals.stockAdjustmentViewModal, data: { selectedItems: payload.selectedItems } },
      { root: true }
    );
  }    

  
  @Action
  toggleField(fieldChange) {
    this.setFieldStatus(fieldChange);
  }

  @Mutation
  setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Action 
  resetData() {
      this.resetList();
  }

  @Mutation
  resetList() {
    this.pageData.totalItems = 0;
    this.pageData.totalPages = 0;
    this.data = null;
  }

  @Mutation
  setFilters(filters: StockAdjustmentListFilters)
  {
      this.filters = filters;
  }

  @Mutation
  switchSelectionStatus(id: number) {
    if(this.data == null)
      return;  

    var index = this.data.findIndex(function(item) {
        return item.id === id;
    });

    if (index < 0) {
        return;
    }

    const existing = this.data[index];

    existing.selected = !existing.selected;

    this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
  }

  @Mutation
  switchDetailSelectionStatus(id: number) {
    if(this.currentStockAdjustmentDetails == null)
      return;  

    var index = this.currentStockAdjustmentDetails.findIndex(function(item) {
        return item.id === id;
    });

    if (index < 0) {
        return;
    }

    const existing = this.currentStockAdjustmentDetails[index];

    existing.selected = !existing.selected;

    this.currentStockAdjustmentDetails = [...this.currentStockAdjustmentDetails.slice(0, index), existing, ...this.currentStockAdjustmentDetails.slice(index + 1)];
  }

  @Mutation
  setList(response: AxiosResponse<StockAdjustmentListItem[]>) {
      this.pageData.totalItems = response.headers.totalitems;
      this.pageData.totalPages = response.headers.totalpages;
      if (this.data) {
          this.data = this.data.concat(response.data);
      } else {
          this.data = response.data;
      }
  }

  @Mutation
  setCurrentStockAdjustment(data : StockAdjustmentResource) {
    this.loading = false;
    this.currentStockAdjustment = data;
    this.currentStockAdjustmentDetails = null;
  } 

  @Mutation
  setCurrentStockAdjustmentDetails(data : StockAdjustmentDetailResource[]) {
    this.loading = false;
    this.currentStockAdjustmentDetails = data;
  } 


  @Mutation
  setDataSort(key) {
    if (this.dataSortKey === key) this.dataSortAscending = !this.dataSortAscending;
    else {
      this.dataSortKey = key;
      this.dataSortAscending = true;
    }
  } 

  @Mutation
  setFieldStatus(fieldChange) {
    this.fields[fieldChange.idx].hidden = fieldChange.value;
  }

  @Mutation
  setCurrentPage(number: number) {
    this.pageData.currentPage = number;
  }
  
  @Mutation
  refreshStockAdjustmentData(stockAdjustment) {
    if(this.data)
    {
      var index = this.data.findIndex(function(item) {
          return item.id === stockAdjustment.id;
      });

      if (index < 0) {
          return;
      }

      this.data = [...this.data.slice(0, index), stockAdjustment, ...this.data.slice(index + 1)]
    }
  }     
  
  get getSelectedItems(): Array<StockAdjustmentListItem> {
    if (!this.data) return [];
    return this.data.filter(x => x.selected);
  }

  get getSelectedItemCount(): number {
      return this.getSelectedItems.length;
  }

  get getSelectedDetails(): Array<StockAdjustmentDetailListItem> {
    if (!this.currentStockAdjustmentDetails) return [];
    return this.currentStockAdjustmentDetails.filter(x => x.selected);
  }

  get getSelectedDetailCount(): number {
    return this.getSelectedDetails.length;
  }
}
